/**
 * [MCV-1515] Phone input normalisation.
 *
 * Clerk stores phone numbers in E.164 (`+15551234567`) and rejects anything
 * else. The sign-up form validated that format directly, so a US user typing
 * their number the way it is written everywhere else — `(555) 123-4567`,
 * `555-123-4567`, `555.123.4567` — was rejected with no way to tell what was
 * wrong. Reported as "it doesn't like my phone number and I don't know how to
 * fix it"; the `e.g. +15551234567` hint on the field was not enough.
 *
 * Rather than demand the user transcribe into E.164, accept how people
 * actually type and convert.
 */

/** E.164: a leading `+`, a non-zero country digit, then up to 14 more. */
export const E164_PATTERN = /^\+[1-9]\d{6,14}$/;

export const PHONE_FORMAT_MESSAGE =
  'Enter a 10-digit US number like (555) 123-4567, or include the country code for elsewhere, e.g. +44 20 7946 0958';

/**
 * Best-effort conversion of typed input to E.164.
 *
 * Deliberately conservative: anything that is not confidently recognisable is
 * returned with separators stripped and left for the pattern to reject, rather
 * than guessed at. Silently turning an unrecognised number into a valid-looking
 * one would send a verification code to the wrong phone.
 */
export const normalizePhoneNumber = (input: string): string => {
  if (!input) return '';

  const trimmed = input.trim();
  // Keep a leading `+`, drop every other non-digit (spaces, dashes, parens,
  // dots and the `(0)` some international formats carry).
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');

  if (!digits) return '';

  // Already international — the user supplied the country code themselves.
  if (hasPlus) return `+${digits}`;

  // Bare 10-digit US/Canada number: the overwhelmingly common case here.
  if (digits.length === 10) return `+1${digits}`;

  // 11 digits starting with the NANP country code.
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;

  // Something else — hand it back normalised but unprefixed so validation
  // fails and the user is told what to enter, instead of us inventing a
  // country code for them.
  return digits;
};
